import {
  chairsOverCapacity,
  overSeatedArrangements,
  seatingOf,
  type OverSeatedArrangement,
  type Seating,
} from '@lib/helpers/furniture-capacity'
import type { FurnitureItemDto } from '@lib/dto/furniture-item.dto'
import type { FurnitureGroupDto } from '@lib/dto/furniture-group.dto'
import type { FurnitureGroupMemberInput } from '@lib/input/furniture-group.input'

/** What an arrangement being built seats, and by how much it overshoots. */
export interface GroupSeating {
  seating: Seating
  /** Chairs beyond the tables' seats, or null when there is nothing to say. */
  over: number | null
}

/**
 * Resolve an arrangement's members to their catalog pieces and tally the seating.
 *
 * Shared by the venue and vendor panels rather than written in each: the API
 * refuses an over-seated arrangement, and a form that disagrees with the API
 * about which arrangements those are is worse than no check at all. A member
 * whose item is not in `items` is skipped, matching the API — it drops members
 * that are not the arrangement's own venue's pieces before it counts.
 */
export function groupSeating(items: FurnitureItemDto[], members: FurnitureGroupMemberInput[]): GroupSeating {
  const byId = new Map(items.map((i) => [i.id, i]))
  const seating = seatingOf(
    members.flatMap((m) => {
      const item = byId.get(m.furnitureItemId)
      return item ? [{ class: item.class, maxCapacity: item.maxCapacity }] : []
    }),
  )
  return { seating, over: chairsOverCapacity(seating) }
}

/**
 * The existing arrangements a proposed seat count would break.
 *
 * Asked while somebody is typing a capacity into the item form, so the answer
 * has to be about a number that is not saved yet: the piece being edited is
 * weighed at `proposedCapacity` and every other member at what it already
 * carries. The API refuses the same edit — this is so the refusal arrives while
 * the field is still under the cursor, naming what to go and fix, rather than
 * after a save that looked fine.
 */
export function arrangementsBrokenBy(
  groups: FurnitureGroupDto[],
  itemId: string,
  proposedCapacity: number | null,
): OverSeatedArrangement[] {
  if (proposedCapacity === null) return []
  return overSeatedArrangements(
    groups
      .filter((g) => g.members.some((m) => m.furnitureItemId === itemId))
      .map((g) => ({
        id: g.id,
        name: g.name,
        pieces: g.members.map((m) =>
          m.furnitureItemId === itemId
            ? { class: 'Table' as const, maxCapacity: proposedCapacity }
            : { class: m.item.class, maxCapacity: m.item.maxCapacity },
        ),
      })),
  )
}
